import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { Building, Gift, Eye, Users, TrendingUp, Plus, ArrowRight, MessageSquare } from 'lucide-react';
import { formatDate } from '../../lib/utils';

export default function ResourcePartnerDashboard() {
  const { user, axiosAuth } = useAuth();
  const [org, setOrg] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const api = axiosAuth();

  useEffect(() => {
    Promise.all([
      api.get('/partner-organizations/mine').catch(() => ({ data: null })),
      api.get('/insights/resource-partner').catch(() => ({ data: null }))
    ]).then(([orgRes, insightsRes]) => {
      setOrg(orgRes.data);
      setInsights(insightsRes.data);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const hasOrg = org && org.id;

  return (
    <div className="space-y-6" data-testid="resource-partner-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
            Welcome, {user.full_name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground mt-1 text-sm md:text-base">
            {hasOrg ? org.name : 'Community Resource Partner Dashboard'}
          </p>
        </div>
        <Button 
          onClick={() => navigate('/post-opportunity')} 
          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-6 gap-2 shrink-0"
          data-testid="post-opportunity-button"
        >
          <Plus size={16} /> Post Opportunity
        </Button>
      </div>

      {/* Setup Prompt if no org */}
      {!hasOrg && (
        <Card className="border-2 border-dashed border-emerald-200 bg-emerald-50/50 rounded-2xl">
          <CardContent className="p-6 text-center">
            <Building className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
            <h3 className="font-semibold text-lg text-emerald-900">Complete Your Organization Profile</h3>
            <p className="text-emerald-700 text-sm mt-1 mb-4">Set up your organization to start posting opportunities and connecting with participants.</p>
            <Button onClick={() => navigate('/organization-profile')} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full" data-testid="partner-create-profile">
              Create Profile <ArrowRight size={16} className="ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Card 
          className="border-0 shadow-sm bg-white rounded-2xl cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/my-opportunities?status=active')}
          data-testid="partner-stat-active"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                <Gift className="w-4 h-4 text-emerald-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-foreground">{insights?.active_opportunities || 0}</p>
            <p className="text-xs text-muted-foreground">Active Opportunities</p>
          </CardContent>
        </Card>
        <Card 
          className="border-0 shadow-sm bg-white rounded-2xl cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/my-opportunities?sort=views')}
          data-testid="partner-stat-views"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center">
                <Eye className="w-4 h-4 text-sky-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-foreground">{insights?.total_views || 0}</p>
            <p className="text-xs text-muted-foreground">Total Views</p>
          </CardContent>
        </Card>
        <Card 
          className="border-0 shadow-sm bg-white rounded-2xl cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/my-opportunities?sort=interests')}
          data-testid="partner-stat-interests"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-amber-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-foreground">{insights?.total_interests || 0}</p>
            <p className="text-xs text-muted-foreground">Interests Received</p>
          </CardContent>
        </Card>
        <Card 
          className="border-0 shadow-sm bg-white rounded-2xl cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/referrals')}
          data-testid="partner-stat-referrals"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
                <Users className="w-4 h-4 text-violet-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-foreground">{insights?.total_referrals || 0}</p>
            <p className="text-xs text-muted-foreground">Referrals Received</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-4 md:gap-6">
        {/* Organization Summary */}
        {hasOrg && (
          <Card 
            className="border-0 shadow-sm rounded-2xl cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/organization-profile')}
            data-testid="partner-org-card"
          >
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Organization Profile</CardTitle>
              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); navigate('/organization-profile'); }} className="text-emerald-600 text-xs" data-testid="partner-org-edit">Edit</Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                    <Building className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium">{org.name}</p>
                    <Badge className="bg-emerald-50 text-emerald-700 mt-1">{org.category}</Badge>
                  </div>
                </div>
                {org.description && <p className="text-sm text-muted-foreground line-clamp-2">{org.description}</p>}
                <div className="text-xs text-muted-foreground space-y-1">
                  {org.service_area && <p>Service Area: {org.service_area}</p>}
                  {org.hours && <p>Hours: {org.hours}</p>}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Opportunities */}
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Recent Opportunities</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/my-opportunities')} className="text-emerald-600 text-xs" data-testid="partner-recent-view-all">View all</Button>
          </CardHeader>
          <CardContent>
            {insights?.recent_opportunities?.length > 0 ? (
              <div className="space-y-3">
                {insights.recent_opportunities.slice(0, 4).map(opp => (
                  <div 
                    key={opp.id} 
                    className="flex items-center gap-3 p-2.5 rounded-xl bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => navigate('/my-opportunities')}
                    data-testid={`partner-opportunity-${opp.id}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center shrink-0">
                      <Gift className="w-5 h-5 text-sky-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{opp.title}</p>
                      <p className="text-xs text-muted-foreground">{opp.views} views · {opp.interests} interested</p>
                    </div>
                    <Badge className={opp.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                      {opp.status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No opportunities posted yet.</p>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-2">
            <Button variant="outline" className="h-auto py-3 flex-col gap-1.5 rounded-xl hover:bg-emerald-50 hover:border-emerald-200" onClick={() => navigate('/post-opportunity')} data-testid="quick-post">
              <Gift size={18} className="text-emerald-600" />
              <span className="text-xs">Post Opportunity</span>
            </Button>
            <Button variant="outline" className="h-auto py-3 flex-col gap-1.5 rounded-xl hover:bg-sky-50 hover:border-sky-200" onClick={() => navigate('/my-opportunities')} data-testid="quick-opportunities">
              <Eye size={18} className="text-sky-600" />
              <span className="text-xs">View Opportunities</span>
            </Button>
            <Button variant="outline" className="h-auto py-3 flex-col gap-1.5 rounded-xl hover:bg-amber-50 hover:border-amber-200" onClick={() => navigate('/referrals')} data-testid="quick-referrals">
              <Users size={18} className="text-amber-600" />
              <span className="text-xs">View Referrals</span>
            </Button>
            <Button variant="outline" className="h-auto py-3 flex-col gap-1.5 rounded-xl hover:bg-violet-50 hover:border-violet-200" onClick={() => navigate('/messages')} data-testid="quick-messages">
              <MessageSquare size={18} className="text-violet-600" />
              <span className="text-xs">Messages</span>
            </Button>
          </CardContent>
        </Card>

        {/* Community Impact */}
        <Card 
          className="border-0 shadow-sm rounded-2xl cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/insights')}
          data-testid="partner-community-impact"
        >
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Community Impact</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-4">
              <p className="text-3xl font-bold text-emerald-600">{(insights?.total_views || 0) + (insights?.total_interests || 0) + (insights?.total_referrals || 0)}</p>
              <p className="text-sm text-muted-foreground mt-1">Total Engagements</p>
              <p className="text-xs text-muted-foreground mt-2">Your opportunities are making a difference in the community!</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
